import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CardInfo } from '@lub0v/santa-utils';
import { Button } from '@src/components/Button/Button';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import '@src/components/DateRangeCalendar/DateRangePicker.css';

export function HistoryParticipantFilter({ cards, value, onChange, disabled = false }: { disabled?: boolean; cards: CardInfo[]; value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const id = useId();
  const selected = cards.find(card => String(card.id) === value);
  const filtered = cards.filter(card => card.username.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  useLayoutEffect(() => {
    if (!open || !menu.current) return;
    const popup = menu.current;
    popup.showPopover();
    const position = () => {
      if (!trigger.current) return;
      const anchor = trigger.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const leftEdge = (viewport?.offsetLeft ?? 0) + 12;
      const topEdge = (viewport?.offsetTop ?? 0) + 12;
      const rightEdge = leftEdge + (viewport?.width ?? window.innerWidth) - 24;
      const bottomEdge = topEdge + (viewport?.height ?? window.innerHeight) - 24;
      popup.style.width = `${Math.min(302, rightEdge - leftEdge)}px`;
      const below = Math.max(0, bottomEdge - anchor.bottom - 8);
      const above = Math.max(0, anchor.top - topEdge - 8);
      const placeAbove = below < 320 && above > below;
      popup.style.maxHeight = `${placeAbove ? above : below}px`;
      const height = popup.getBoundingClientRect().height;
      popup.style.left = `${Math.max(leftEdge, Math.min(anchor.left, rightEdge - popup.getBoundingClientRect().width))}px`;
      popup.style.top = `${placeAbove ? Math.max(topEdge, anchor.top - height - 8) : anchor.bottom + 8}px`;
    };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    window.visualViewport?.addEventListener('resize', position);
    window.visualViewport?.addEventListener('scroll', position);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      window.visualViewport?.removeEventListener('resize', position);
      window.visualViewport?.removeEventListener('scroll', position);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    search.current?.focus();
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  function choose(next: string) {
    onChange(next);
    setOpen(false);
    trigger.current?.focus();
  }
  return <div className="history-participant-filter" ref={root} onKeyDown={event => {
    if (event.key === 'Escape' && open) { event.stopPropagation(); setOpen(false); trigger.current?.focus(); }
  }} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <Button disabled={disabled} ref={trigger} size="small" variant="secondary" aria-haspopup="dialog" aria-expanded={open} aria-controls={id}
      aria-label={t('participantProgress.filterParticipant')} icon={<ChevronDownIcon />} iconPosition="end"
      onClick={() => { setQuery(''); setOpen(!open); }}>
      {selected?.username || (value === 'management' ? t('historyFilters.management') : '') || t('historyFilters.all')}
    </Button>
    {open && <div ref={menu} popover="manual" id={id} role="dialog" aria-label={t('participantProgress.filterParticipant')} className="history-participant-filter__menu">
      <input ref={search} type="search" value={query} onChange={event => setQuery(event.target.value)}
        placeholder={t('box.organizer.searchPlaceholder')} aria-label={t('box.organizer.searchPlaceholder')} />
      <div className="history-participant-filter__options">
        <button type="button" aria-pressed={!value} onClick={() => choose('')}>{t('historyFilters.all')}</button>
        <button type="button" aria-pressed={value === 'management'} onClick={() => choose('management')}>{t('historyFilters.management')}</button><hr />
        {filtered.map(card => <button type="button" key={card.id} aria-pressed={value === String(card.id)} onClick={() => choose(String(card.id))}>{card.username}</button>)}
        {!filtered.length && <p role="status">{t('participantProgress.noParticipants')}</p>}
      </div>
    </div>}
  </div>;
}
